import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
} from "../../../../components";
import {
  CountryField,
  CountryFieldDetails,
  TextFieldWithValidation,
} from "../../../../components/textFieldWithValidation/TextFieldWithValidation";
import {
  MAX_LENGTH_50,
  MAX_LENGTH_75,
  PAGE_IDS,
} from "../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import { getVatPostalDetailsFromCountryKey } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useSyncFormValidity } from "../../../../hooks/useSyncFormValidity/useSyncFormValidity";
import { editClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { CreateClientFormKey } from "../../modelsAndSchemas/Models";
import { createClientSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateClientMailingAddressFormWrapper } from "./CreateClientMailingAddressForm.styles";
import { getFieldRuleByCountry } from "../../../../helpers/constants/countryFieldRulesUtils";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";

const pageId = PAGE_IDS.CREATE_CLIENT_MAILING_ADDRESS;

interface CreateClientMailingAddressFormProps {
  countryVatPostalData?: CountryField[];
  isFormReset?: boolean;
}

export const CreateClientMailingAddressForm: FC<
  CreateClientMailingAddressFormProps
> = ({ countryVatPostalData, isFormReset }) => {
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const {
    fetchLabel,
    commonLabels,
    commonValidationMessages,
    fetchValidationMessage,
    fetchDropdownValuesByPrefix,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { isMobile } = useMediaQueries();

  const { control, getValues } = useFormContext();
  const { isEdit: isEditClient } = useSelector(editClientSelector);
  const { addressCountry } = getValues();

  const watchPostalCode = useWatch({
    name: CreateClientFormKey.postalCode,
  });

  const getLabel = (key: string) => {
    return fetchLabel(createClientSiteCoreKeys.clientMailingAddressTitle, key);
  };

  const [isDefaultPostalCodeSet, setIsDefaultPostalCodeSet] = useState(false);

  const postalCodeRef = useRef<{
    setDefaultValue: (defaultValue: string) => void;
  }>(null);

  useEffect(() => {
    const { postalCode } = getValues();
    if (isEditClient && !isDefaultPostalCodeSet && postalCode) {
      postalCodeRef.current?.setDefaultValue(postalCode ?? "");
      setIsDefaultPostalCodeSet(true);
    }
  }, [isEditClient, watchPostalCode]);

  const defaultCountryData: CountryFieldDetails = {
    label: getLabel(createClientSiteCoreKeys.postalCode),
    placeHolder: commonLabels.inputPlaceHolder,
    maxLength: 10,
    regex: ".*",
    regexNotAllowed: "",
    formatErrorMessage: "",
    blurErrorMessage: "",
  };

  const mandatoryFields = [
    CreateClientFormKey.address1,
    CreateClientFormKey.city,
  ];

  const {
    formState: { errors },
  } = useFormContext();

  useSyncFormValidity({
    formKey: "createClientMailingAddressForm",
    mandatoryFields,
    errors,
    isMobile,
  });

  const stateProvinceRule = getFieldRuleByCountry(
    addressCountry,
    CreateClientFormKey.stateProvince,
  );

  const countrySpecificOptions = useMemo(() => {
    const stateOptions = fetchDropdownValuesByPrefix(
      createClientSiteCoreKeys.clientMailingAddressTitle,
      createClientSiteCoreKeys.stateRegionProvinceListPrefix,
      addressCountry,
    );

    return { stateOptions };
  }, [addressCountry, siteCoreLoading]);

  const tooltipMessages = useMemo(() => {
    return {
      tooltipMessage:
        commonValidationMessages.fieldSpecialCharactersTooltipMessage,

      tooltipHeaderTitle:
        commonValidationMessages.allowedSpecialCharactersTitle,
    };
  }, [commonValidationMessages]);

  return (
    <CreateClientMailingAddressFormWrapper>
      <form>
        <MuiGrid container className="main-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateClientFormKey.address1}
            >
              <MuiTextField
                isMandatory
                id={`${pageId}-${CreateClientFormKey.address1}`}
                label={getLabel(createClientSiteCoreKeys.address1)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_50}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={tooltipMessages.tooltipMessage}
                tooltipHeaderTitle={tooltipMessages.tooltipHeaderTitle}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateClientFormKey.address2}
            >
              <MuiTextField
                id={`${pageId}-${CreateClientFormKey.address2}`}
                label={getLabel(createClientSiteCoreKeys.address2)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_50}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={tooltipMessages.tooltipMessage}
                tooltipHeaderTitle={tooltipMessages.tooltipHeaderTitle}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateClientFormKey.address3}
            >
              <MuiTextField
                id={`${pageId}-${CreateClientFormKey.address3}`}
                label={getLabel(createClientSiteCoreKeys.address3)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_50}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={tooltipMessages.tooltipMessage}
                tooltipHeaderTitle={tooltipMessages.tooltipHeaderTitle}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateClientFormKey.countryName}
            >
              <MuiTextField
                id={`${pageId}-${CreateClientFormKey.country}`}
                label={getLabel(createClientSiteCoreKeys.country)}
                placeholder={commonLabels.selectPlaceHolder}
                disabled
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController control={control} name={CreateClientFormKey.city}>
              <MuiTextField
                isMandatory
                id={`${pageId}-${CreateClientFormKey.city}`}
                label={getLabel(createClientSiteCoreKeys.city)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_50}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={tooltipMessages.tooltipMessage}
                tooltipHeaderTitle={tooltipMessages.tooltipHeaderTitle}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateClientFormKey.stateProvince}
            >
              {stateProvinceRule.mandatory || stateProvinceRule.showDropdownForOptionalCountry ? (
                <MuiSelect
                  label={getLabel(createClientSiteCoreKeys.stateProvince)}
                  options={countrySpecificOptions.stateOptions}
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${CreateClientFormKey.stateProvince}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  isMandatory={stateProvinceRule.mandatory}
                />
              ) : (
                <MuiTextField
                  id={`${pageId}-${CreateClientFormKey.stateProvince}`}
                  label={getLabel(createClientSiteCoreKeys.stateProvince)}
                  placeholder={commonLabels.stateProvincePlaceHolder}
                  maxLength={MAX_LENGTH_75}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={tooltipMessages.tooltipMessage}
                  tooltipHeaderTitle={tooltipMessages.tooltipHeaderTitle}
                />
              )}
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateClientFormKey.postalCode}
              ref={postalCodeRef}
            >
              <TextFieldWithValidation
                id={`${pageId}-${CreateClientFormKey.postalCode}`}
                formId={CreateClientFormKey.postalCode}
                isCountryChanged={addressCountry}
                zeroValidationMessage={fetchValidationMessage(
                  commonSiteCoreKeys.greaterThanZeroFieldValidator,
                )}
                requiredFieldMessage={fetchValidationMessage(
                  commonSiteCoreKeys.requiredValidation,
                )}
                isFormReset={isFormReset}
                fieldDetails={{
                  ...(getVatPostalDetailsFromCountryKey(
                    addressCountry,
                    countryVatPostalData,
                  )?.postalCode || defaultCountryData),
                }}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </form>
    </CreateClientMailingAddressFormWrapper>
  );
};
